import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, Download, TrendingUp, FileText, Eye, Calendar } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

import { apiService } from '@/services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface AnalyticsOverview {
  total_posts: number
  published_posts: number
  draft_posts: number
  scheduled_posts: number
  total_views: number
  total_categories: number
  total_tags: number
  avg_views_per_post: number
  most_viewed_post: {
    id: string
    title: string
    slug: string
    view_count: number
  } | null
  recent_posts_count: number
}

interface PostPerformance {
  id: string
  title: string
  slug: string
  view_count: number
  published_at: string | null
  days_since_published: number | null
  avg_views_per_day: number | null
  categories: string[]
  tags: string[]
}

interface CategoryPerformance {
  category_id: string
  category_name: string
  category_slug: string
  post_count: number
  total_views: number
  avg_views_per_post: number
}

interface PerformanceData {
  top_posts: PostPerformance[]
  category_performance: CategoryPerformance[]
  views_over_time: { date: string; views: number; posts_published: number }[]
  trending_tags: { tag_id: string; tag_name: string; tag_slug: string; post_count: number }[]
}

export function BlogAnalytics() {
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv')
  const [timePeriod, setTimePeriod] = useState<'7days' | '30days' | '90days' | 'all'>('30days')

  // Fetch overview data
  const {
    data: overview,
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useQuery<AnalyticsOverview>({
    queryKey: ['blog-analytics-overview'],
    queryFn: () => apiService.blog.analytics.overview(),
  })

  // Fetch performance data
  const {
    data: performance,
    isLoading: performanceLoading,
    refetch: refetchPerformance,
  } = useQuery<PerformanceData>({
    queryKey: ['blog-analytics-performance', timePeriod],
    queryFn: () => apiService.blog.analytics.performance({ period: timePeriod }),
  })

  const handleRefresh = () => {
    refetchOverview()
    refetchPerformance()
  }

  const handleExport = async () => {
    try {
      const blob = await apiService.blog.analytics.export({ format: exportFormat })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `blog-analytics-${new Date().toISOString().split('T')[0]}.${exportFormat}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export analytics:', error)
    }
  }

  const isLoading = overviewLoading || performanceLoading

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Article Analytics</h1>
          <p className="text-muted-foreground">
            Track performance, engagement, and content insights
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={exportFormat} onValueChange={(val) => setExportFormat(val as 'csv' | 'json')}>
            <SelectTrigger className="w-32 bg-background border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleExport}
            className="border-border hover:bg-accent"
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="border-border hover:bg-accent"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-card text-card-foreground border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Total Posts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {overview?.total_posts || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview?.published_posts || 0} published
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card text-card-foreground border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {overview?.total_views.toLocaleString() || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview?.avg_views_per_post.toFixed(1) || 0} avg per post
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card text-card-foreground border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Categories</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {overview?.total_categories || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview?.total_tags || 0} tags
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card text-card-foreground border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Recent Posts</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {overview?.recent_posts_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Posts */}
      <Card className="bg-card text-card-foreground border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-foreground">Top Performing Posts</CardTitle>
              <CardDescription className="text-muted-foreground">
                Most viewed blog posts
              </CardDescription>
            </div>
            <Select value={timePeriod} onValueChange={(val) => setTimePeriod(val as any)}>
              <SelectTrigger className="w-32 bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7days">7 Days</SelectItem>
                <SelectItem value="30days">30 Days</SelectItem>
                <SelectItem value="90days">90 Days</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="text-foreground">Title</TableHead>
                  <TableHead className="text-foreground">Views</TableHead>
                  <TableHead className="text-foreground">Avg/Day</TableHead>
                  <TableHead className="text-foreground">Categories</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {performance?.top_posts.slice(0, 10).map((post) => (
                  <TableRow
                    key={post.id}
                    className="border-b border-border hover:bg-accent/50"
                  >
                    <TableCell className="font-medium text-foreground">
                      <a
                        href={`/blog/posts?id=${post.id}`}
                        className="hover:underline text-foreground"
                      >
                        {post.title}
                      </a>
                    </TableCell>
                    <TableCell className="text-foreground">
                      {post.view_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {post.avg_views_per_day?.toFixed(1) || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {post.categories.slice(0, 2).map((cat, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="bg-muted text-muted-foreground"
                          >
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Category Performance */}
      <Card className="bg-card text-card-foreground border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Category Performance</CardTitle>
          <CardDescription className="text-muted-foreground">
            Views and engagement by category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performance?.category_performance || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="category_name"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  color: 'hsl(var(--foreground))',
                }}
              />
              <Legend />
              <Bar
                dataKey="total_views"
                fill="hsl(var(--primary))"
                name="Total Views"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="post_count"
                fill="hsl(var(--muted))"
                name="Post Count"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Trending Tags */}
      <Card className="bg-card text-card-foreground border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Trending Tags</CardTitle>
          <CardDescription className="text-muted-foreground">
            Most used tags across posts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {performance?.trending_tags.map((tag) => (
              <Badge
                key={tag.tag_id}
                variant="outline"
                className="border-border text-foreground bg-background"
              >
                {tag.tag_name} <span className="ml-1 text-muted-foreground">({tag.post_count})</span>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
